import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useUpdateTelegramInfoMutation,
  useDisconnectTelegramMutation,
  useLazyGetTelegramConnectionStatusQuery,
} from '../../../features/apis/intergration';
import useCurrentUser from '../../../customHook/useCurrentUser';
import ReactToastify from '../../../customHook/useReactToastify';
import { FaArrowRightLong } from 'react-icons/fa6';
import SelectSkeleton from '../SelectSkeleton';
import TelegramIcon from '../../../assets/images/telegram.svg';

const TelegramSelect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [updateTelegramInfo, { isLoading }] = useUpdateTelegramInfoMutation();
  const [disconnectTelegram] = useDisconnectTelegramMutation();
  const [getConnectionStatus, { data: connectionStatus }] =
    useLazyGetTelegramConnectionStatusQuery();
  const user = useCurrentUser();
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [username, setUsername] = useState('');

  const params = new URLSearchParams(location.search);
  const companyId = params.get('companyId') || user?.companyId || '';

  useEffect(() => {
    if (companyId) {
      getConnectionStatus(companyId)
        .unwrap()
        .then((res: any) => {
          if (res?.isConnected) {
            setUsername(res?.username || '');
          }
        })
        .catch(() => {});
    }
  }, [companyId, getConnectionStatus]);

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!botToken || !chatId || !companyId) {
      ReactToastify({
        success: false,
        message: 'Bot token and chat ID are required',
      });
      return;
    }

    try {
      await updateTelegramInfo({
        companyId,
        data: {
          botToken,
          chatId,
          username,
        },
      }).unwrap();

      ReactToastify({
        success: true,
        message: 'Telegram info updated successfully',
      });

      navigate('/dashboard/ask-tara');
    } catch (err) {
      console.error('Telegram update failed:', err);
      ReactToastify({
        success: false,
        message: 'Something went wrong while saving Telegram info',
      });
    }
  };

  const handleDisconnect = async () => {
    if (!companyId) return;
    try {
      await disconnectTelegram(companyId).unwrap();
      setBotToken('');
      setChatId('');
      setUsername('');
      ReactToastify({ success: true, message: 'Telegram disconnected' });
    } catch (err) {
      ReactToastify({
        success: false,
        message: 'Failed to disconnect Telegram',
      });
    }
  };

  return (
    <div className="relative w-full h-screen  overflow-hidden font-inter">
      <div className="flex items-center justify-center flex-col h-full">
        <div className="bg-white w-3/6  h-auto min-h-72 p-10 shadow-lg rounded-md border-[0.5px] border-[#eae8e8]">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={TelegramIcon} alt="Telegram" className="h-8 w-8" />
              <h1 className="text-2xl font-bold">Telegram</h1>
            </div>
            {connectionStatus?.isConnected && (
              <button
                type="button"
                onClick={handleDisconnect}
                className="px-3 py-1.5 text-red-600 border border-red-600 rounded hover:bg-red-50 transition text-sm"
              >
                Disconnect
              </button>
            )}
          </div>

          {!isLoaded ? (
            <SelectSkeleton />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-2 text-xs font-roboto pl-2 block">
                Bot Token
              </label>
              <input
                type="text"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
                required
              />
            </div>

            <div>
              <label className="mb-2 text-xs font-roboto pl-2 block">
                Chat ID
              </label>
              <input
                type="text"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="-1001234567890 or @channelusername"
                className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
                required
              />
            </div>

            <div>
              <label className="mb-2 text-xs font-roboto pl-2 block">
                Username (optional)
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="@yourchannel"
                className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
              />
            </div>

            <div className="flex justify-end items-center mt-4">
              <button
                type="submit"
                disabled={isLoading || !botToken || !chatId}
                className="bg-gradient-to-r from-[#6E3EF5] to-[#A818D8] hover:from-[#6E3EF5]/80 hover:to-[#A818D8]/80 flex items-center justify-center px-4 py-3 font-inter rounded-md text-white gap-2 hover:scale-105 hover:shadow text-xs md:text-sm add-my-team-btn disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Submit'} <FaArrowRightLong />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
    </div>
  );
};

export default TelegramSelect;
